import React, { useEffect, useState } from 'react';
import { useThemeContext } from '@ThemeContext';
import { classNames, isMobileView } from '@utils';

// Components
import Skeleton from 'react-loading-skeleton';
import AccordionItemWlkContract from './AccordionItemWlkContract';
import { Badge, Checkbox, Panel } from '@ftdr/blueprint-components-react';
import { WarrantylinkStatus } from '@constants/dashboardFilters';
import { IsTheme, Theme } from '@app/core/featureToggle';
import REText from '@components/wrappedBDS/REText';
import { getCurrentThemeConfig } from '@app/core/theme-config';

const AccordionItemWarranty = (props) => {
  const theme = useThemeContext();
  const [isChecked, setIsChecked] = useState(isContractSelected);
  const [tagColor, setTagColor] = useState('gray');
  const GridColumnTitle = {
    PropertyAddress: 'Covered Property Address',
    Office: 'Office',
    Agent: 'Agent',
    ContractNumber: 'Plan #',
  };

  const submittableStatuses = [WarrantylinkStatus.READY, WarrantylinkStatus.SAVED];
  const isMobile = isMobileView();

  useEffect(() => {
    if (props.status) setTagColor(getStatusColor());
  }, [props.status]);

  useEffect(() => {
    if (!props.isActive && !props.skeleton) {
      setEdit(false);
    }
  }, [props.isActive]);

  /** Update the checkbox if selection was changed */
  useEffect(() => {
    setIsChecked(isContractSelected);
  }, [props.filteredList, props.selectedContracts]);

  function handleCheckboxClick(e) {
    setIsChecked(!isChecked);
    props.onCheckboxClick(props.contract);
  }

  async function handleSave(buyer, seller) {
    const success = await props.onSaveForm({
      id: props.contractId,
      warrantylink: {
        form: { buyer, seller },
      },
    });

    if (success) {
      setEdit(false);
    }
    return success;
  }

  function isContractSelected() {
    return props.selectedContracts && props.selectedContracts.includes(props.contractId.toString());
  }

  function isEditable() {
    return (props.status === 'Ready' || props.status === 'Saved') && props.isActive;
  }

  function setEdit(editState) {
    props.toggleEdit(props.contractId, editState);
  }

  function getStatusColor() {
    switch (props.status) {
      case 'Ready':
        return 'success';
      case 'Saved':
        return 'success';
      case 'Submitted':
        return 'primary';
      case 'Pending':
        return 'primary';
      case 'Complete':
        return 'gray';
      default:
        return 'gray';
    }
  }

  const getSubmissionHeading = () => {
    if (
      [
        WarrantylinkStatus.SUBMITTED,
        WarrantylinkStatus.COMPLETED,
        WarrantylinkStatus.PENDING,
      ].includes(props.status)
    ) {
      return IsTheme(Theme.Ahs2024) ? 'Submitted Date' : 'SUBMITTED DATE';
    }

    return IsTheme(Theme.Ahs2024) ? 'Submission Deadline' : 'SUBMISSION DEADLINE';
  };

  const getSubmissionValue = () => {
    if (
      [
        WarrantylinkStatus.SUBMITTED,
        WarrantylinkStatus.COMPLETED,
        WarrantylinkStatus.PENDING,
      ].includes(props.status)
    ) {
      return props.submissionDate;
    }

    return props.submissionDeadline;
  };

  return (
    <Panel
      className={classNames([
        getCurrentThemeConfig().wlkAccordianItemPanelClass,
        isChecked
          ? theme === 'hsa'
            ? 'border-primary-400'
            : 'border-secondary-400'
          : 'border-transparent',
      ])}
      border="1"
      borderColor="gray-300"
      rounded={getCurrentThemeConfig().wlkPanelRounded}
      padding="none"
      shadow={getCurrentThemeConfig().wlkAccordianItemShadow}
    >
      <div
        className="accordion-item__heading flex justify-start items-center relative w-full py-3 md:pr-20 pl-4 md:pl-6 text-left text-sm md:text-base bg-transparent cursor-pointer"
        onClick={props.skeleton ? undefined : props.onClick}
        aria-expanded={props.isActive}
        id={props.id}
      >
        <div className="flex flex-row w-full">
          <div className="flex">
            <div className={submittableStatuses.includes(props.status) ? 'flex' : 'flex ml-7'}>
              {props.skeleton ? (
                <Skeleton />
              ) : (
                props.canEditWLKContracts && (
                  <Checkbox
                    className={submittableStatuses.includes(props.status) ? 'flex' : 'hidden'}
                    value={isChecked || false}
                    checked={isChecked || false}
                    onClick={props.skeleton ? () => {} : handleCheckboxClick}
                  />
                )
              )}
            </div>
          </div>

          <div className="flex flex-col w-full sm:pl-6">
            <div className="flex mb-1">
              {props.skeleton ? (
                <Skeleton />
              ) : (
                <Badge color={tagColor} size="large">
                  {props.status}
                </Badge>
              )}
            </div>

            <div
              className={classNames([
                `${isMobile && getCurrentThemeConfig().isWlkDisplayFlex? 'flex-column' : 'sm:flex lg:flex flex-column sm:flex-row sm-max:flex-col'}`,
                `${isMobile ? 'pr-4' : ''}`,
              ])}
            >
              <div
                className={`${isMobile && getCurrentThemeConfig().hasWlkAttributeBottomMargin ? 'mb-4' : ''} flex flex-col w-full m:w-1/3 mr-2`}
              >
                <REText variant="label" color="primary">
                  {getCurrentThemeConfig().isWlkPropertyAttributesInLowerCase
                    ? GridColumnTitle.PropertyAddress
                    : GridColumnTitle.PropertyAddress.toUpperCase()}
                </REText>
                {props.skeleton ? (
                  <Skeleton className="flex w-auto" />
                ) : (
                  <REText variant="caption" color="primary" className="break-words">
                    {props.address}
                  </REText>
                )}
              </div>
              <div
                className={`${isMobile && getCurrentThemeConfig().hasWlkAttributeBottomMargin ? 'mb-4' : ''} flex flex-col w-full m:w-1/4 mr-2`}
              >
                <REText variant="label" color="primary">
                  {getCurrentThemeConfig().isWlkPropertyAttributesInLowerCase
                    ? GridColumnTitle.Office
                    : GridColumnTitle.Office.toUpperCase()}
                </REText>
                {props.skeleton ? (
                  <Skeleton className="flex w-auto" />
                ) : (
                  <REText variant="caption" color="primary" className="break-all">
                    {props.office}
                  </REText>
                )}
              </div>
              <div
                className={`${isMobile && getCurrentThemeConfig().hasWlkAttributeBottomMargin ? 'mb-4' : ''} flex flex-col w-full m:w-1/4 mr-2`}
              >
                <REText variant="label" color="primary">
                  {getCurrentThemeConfig().isWlkPropertyAttributesInLowerCase
                    ? GridColumnTitle.Agent
                    : GridColumnTitle.Agent.toUpperCase()}
                </REText>
                {props.skeleton ? (
                  <Skeleton className="flex w-auto" />
                ) : (
                  <REText variant="caption" color="primary" className="break-words">
                    {props.agent}
                  </REText>
                )}
              </div>
              <div
                className={`${isMobile && getCurrentThemeConfig().hasWlkAttributeBottomMargin ? 'mb-4' : ''} flex flex-col w-full m:w-1/4 mr-2`}
              >
                <REText variant="label" color="primary">
                  {getCurrentThemeConfig().isWlkPropertyAttributesInLowerCase
                    ? GridColumnTitle.ContractNumber
                    : GridColumnTitle.ContractNumber.toUpperCase()}
                </REText>
                {props.skeleton ? (
                  <Skeleton className="flex w-auto" />
                ) : (
                  <REText variant="caption" color="primary">
                    {props.contractNumber}
                  </REText>
                )}
              </div>
              <div
                className={`${isMobile && getCurrentThemeConfig().hasWlkAttributeBottomMargin ? 'mb-4' : ''} flex flex-col w-full m:w-1/4`}
              >
                <REText variant="label" color="primary">
                  {getSubmissionHeading()}
                </REText>
                {props.skeleton ? (
                  <Skeleton className="flex w-auto" />
                ) : (
                  <REText variant="caption" color="primary">
                    {getSubmissionValue()}
                  </REText>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <AccordionItemWlkContract
        isActive={props.isActive}
        isDisabled={!isEditable()}
        isCollapsed={props.isCollapsed}
        seller={props.seller}
        buyer={props.buyer}
        contractNumber={props.contractNumber}
        handleSave={handleSave}
        updateErrors={props.updateErrors}
        contract={props.contract}
        onSaveAndCancel={props.onClick}
        isMobile={props.isMobile}
        className="py-2 md:py-4 md:pr-20 pl-12 md:pl-16 sm-max:p-6"
        canEditWLKContracts={props.canEditWLKContracts}
      />
    </Panel>
  );
};

export default AccordionItemWarranty;
